import React from "react";
import api from "../api/client";
import { ModalShell } from "./ui-helpers";
import { useAuth } from "../context/AuthContext";


export function usePinGate() {
  const { user } = useAuth();
  const [pinOpen, setPinOpen] = React.useState(false);
  const pendingActionRef = React.useRef(null);
  const isSuperAdmin = String(user?.currentRole || "") === "Super Admin";

  const askPinThen = React.useCallback((fn) => {
    if (typeof fn !== "function") return;
    if (isSuperAdmin) {
      pendingActionRef.current = null;
      setPinOpen(false);
      fn();
      return;
    }
    pendingActionRef.current = fn;
    setPinOpen(true);
  }, [isSuperAdmin]);

  const closePin = React.useCallback(() => {
    pendingActionRef.current = null;
    setPinOpen(false);
  }, []);

  const handlePinVerified = React.useCallback(async () => {
    const fn = pendingActionRef.current;
    pendingActionRef.current = null;
    setPinOpen(false);
    if (typeof fn === "function") await fn();
  }, []);

  return { pinOpen, askPinThen, closePin, handlePinVerified };
}

export default function PinModal({ uid, open, onClose, onVerified }) {
  const [pin, setPin] = React.useState("");
  const [err, setErr] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) {
      setPin("");
      setErr("");
      setSubmitting(false);
      return;
    }

    const timer = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(timer);
  }, [open]);

  const verify = async () => {
    if (pin.length !== 6 || submitting) return;
    setSubmitting(true);
    setErr("");
    try {
      const form = new FormData();
      form.append("uid", uid);
      form.append("pin", pin);
      const response = await api.post("/auth/verify_pin", form);
      if (response.data?.ok) {
        onVerified?.();
      } else {
        setErr("Invalid PIN. Please try again.");
      }
    } catch (error) {
      setErr(error?.response?.data?.error || "Could not verify PIN.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      size="sm"
      title="Enter E-PIN"
      subtitle="Cruicial actions require a verified 6-digit E-PIN before they can proceed."
      onClose={submitting ? undefined : onClose}
      actions={(
        <>
          <button className="btn btn-secondary" onClick={onClose} type="button" disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" disabled={pin.length !== 6 || submitting} onClick={verify} type="button">
            {submitting ? "Verifying…" : "Verify"}
          </button>
        </>
      )}
    >
      <label>
        <span>6-digit PIN</span>
        <input
          ref={inputRef}
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="Enter your E-PIN"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              verify();
            }
          }}
        />
      </label>
      <div className="helper-text">This confirmation happens in place and will return you to the current workflow after verification.</div>
      {err ? <div className="banner error">{err}</div> : null}
    </ModalShell>
  );
}
